import React from "react";
import { useRealtime } from "./RealtimeProvider";

/**
 * Live realtime status pill.
 *
 * Renders the current state of the admin realtime hub (SignalR over Redis backplane):
 *  - connected  → a pulsing green dot + the number of admins currently online
 *  - offline    → a muted grey dot + "Hors ligne"
 *
 * State comes from {@link useRealtime}, so the same source feeds the presence bar and the live
 * activity feed — this pill is just the always-visible "is the pipe alive?" affordance. It is placed
 * in the sidebar footer directly above the logout button so an admin can tell at a glance, from any
 * page, whether they are receiving live updates and how many colleagues are editing alongside them.
 *
 * Props:
 *  - `compact`  → dot-only form (no label) for the collapsed sidebar; the full state stays in the
 *                 native tooltip so hovering still tells you online/offline.
 *  - `className`→ extra classes appended to the wrapper.
 */
export const RealtimeIndicator: React.FC<{ className?: string; compact?: boolean }> = ({
	className,
	compact = false,
}) => {
	const { connected, online } = useRealtime();
	const label = connected ? `${online} en ligne` : "Hors ligne";
	const title = connected ? `Connexion temps réel active — ${online} admin(s) en ligne` : "Temps réel hors ligne";
	const dot = (
		<span
			className={`h-2 w-2 shrink-0 rounded-full ${connected ? "bg-emerald-500 animate-pulse" : "bg-slate-300"}`}
			aria-hidden
		/>
	);

	if (compact) {
		return (
			<span
				className={`inline-flex items-center justify-center rounded-full border border-slate-200 bg-white p-1.5 ${className ?? ""}`}
				title={title}
				aria-label={title}
			>
				{dot}
			</span>
		);
	}

	return (
		<span
			className={`inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600 ${className ?? ""}`}
			title={title}
			aria-label={title}
		>
			{dot}
			{label}
		</span>
	);
};
